/**
 * Controlled Operator Resolver — determines which operator the player controls
 * at each point in the timeline.
 *
 * Control is determined solely by explicit user-placed CONTROL events.
 * No skill columns imply control transfer — operators can use any skill when not controlled.
 *
 * Only one operator can be controlled at a time. The first operator (slot-0) starts controlled.
 */
import { NounType } from '../../dsl/semantics';
import { TimelineEvent } from '../../consts/viewTypes';


interface ControlSegment {
  slotId: string;
  startFrame: number;
}

/**
 * Resolve which operator is controlled at each frame.
 * Returns a query function for use by the DSL engine's CONTROLLED determiner.
 */
export function resolveControlledOperator(
  events: readonly TimelineEvent[],
  slotIds: readonly string[],
  initialSlotId?: string,
): (frame: number) => string {
  const defaultSlot = initialSlotId ?? slotIds[0] ?? '';

  // Collect control transfer points
  const transferPoints: { frame: number; slotId: string }[] = [];

  for (const ev of events) {
    if (ev.id === NounType.CONTROL) {
      transferPoints.push({ frame: ev.startFrame, slotId: ev.ownerEntityId });
    }
  }

  // Sort by frame (stable: earlier events first)
  transferPoints.sort((a, b) => a.frame - b.frame);

  // Build control segments — deduplicate consecutive same-slot transfers
  const segments: ControlSegment[] = [{ slotId: defaultSlot, startFrame: 0 }];
  for (const tp of transferPoints) {
    const last = segments[segments.length - 1];
    if (tp.slotId !== last.slotId) {
      segments.push({ slotId: tp.slotId, startFrame: tp.frame });
    }
  }

  // Query function: binary search for the segment containing the frame
  return (frame: number): string => {
    let lo = 0;
    let hi = segments.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (segments[mid].startFrame <= frame) lo = mid;
      else hi = mid - 1;
    }
    return segments[lo].slotId;
  };
}
